import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { Trash2, X } from "lucide-react";
import { GlossaryFormItem } from "Types/Admin/GlossaryFormItem";
import { notifications } from "@mantine/notifications";
import {
  useDeleteGlossaryImageMutation,
  useUploadGlossaryImageMutation,
} from "Services/GlossaryApiSlice";

type Props = {
  item?: GlossaryFormItem | null;
  onSave: (payload: FormData) => Promise<void>;
  onCancel: () => void;
};

type SectionState = {
  title: string;
  description: string;
};

type ValidationErrors = {
  title?: string;
  date?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  pin?: string;
  sections?: string[];
  [key: string]: string | string[] | undefined;
};

type ImageFileWithPreview = {
  file: File;
  preview: string;
  uploading?: boolean;
};

type ExistingImage = {
  id: number;
  url: string;
  deleting?: boolean;
};

const fieldStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  padding: "8px 10px",
  borderRadius: 4,
  border: "1px solid #d9d9d9",
};

const errorStyle: React.CSSProperties = {
  color: "#dc2626",
  fontSize: "0.875rem",
  marginTop: "4px",
};

const MAX_SECTIONS = 8;
const MAX_IMAGES = 6;
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

const GlossaryForm: React.FC<Props> = ({ item, onSave, onCancel }) => {
  const lastSyncedId = useRef<number | null>(null);
  const plant = process.env.REACT_APP_PLANT || "";

  const [date, setDate] = useState("");
  const [title, setTitle] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [country, setCountry] = useState("");
  const [pin, setPin] = useState("");
  const [glossaryDescription, setGlossaryDescription] = useState("");
  const [glossaryDescription2, setGlossaryDescription2] = useState("");

  const [sections, setSections] = useState<SectionState[]>(
    Array.from({ length: MAX_SECTIONS }, () => ({
      title: "",
      description: "",
    })),
  );
  const [visibleSections, setVisibleSections] = useState(1);

  const [existingImages, setExistingImages] = useState<ExistingImage[]>([]);
  const [newImages, setNewImages] = useState<ImageFileWithPreview[]>([]);

  const [errors, setErrors] = useState<ValidationErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [deleteGlossaryImage] = useDeleteGlossaryImageMutation();
  const [uploadGlossaryImage] = useUploadGlossaryImageMutation();

  useEffect(() => {
    return () => {
      newImages.forEach((img) => URL.revokeObjectURL(img.preview));
    };
  }, [newImages]);

  useEffect(() => {
    if (!item?.id) return;
    if (lastSyncedId.current === item.id) return;

    lastSyncedId.current = item.id;

    setTitle(item.title ?? "");
    setGlossaryDescription(item.glossaryDescription ?? "");
    setGlossaryDescription2(item.glossaryDescription2 ?? "");

    setDate(
      item.date ? dayjs(item.date, "DD/MM/YYYY").format("YYYY-MM-DD") : "",
    );

    setAddress(item.address ?? "");
    setCity(item.city ?? "");
    setState(item.state ?? "");
    setCountry(item.country ?? "");
    setPin(item.pin ?? "");

    const mappedSections: SectionState[] = Array.from(
      { length: MAX_SECTIONS },
      (_, i) => ({
        title: String(
          item[`section${i + 1}Title` as keyof GlossaryFormItem] ?? "",
        ),
        description: String(
          item[`section${i + 1}Description` as keyof GlossaryFormItem] ?? "",
        ),
      }),
    );

    setSections(mappedSections);

    let lastVisible = 1;
    for (let i = mappedSections.length - 1; i >= 0; i--) {
      if (mappedSections[i].title || mappedSections[i].description) {
        lastVisible = i + 1;
        break;
      }
    }
    setVisibleSections(lastVisible);

    if (item.imagesWithIds && item.imagesWithIds.length > 0) {
      setExistingImages(
        item.imagesWithIds.map((img: { id: number; url: string }) => ({
          id: img.id,
          url: img.url,
        })),
      );
    } else if (item.imagesPreviewUrls && item.imagesPreviewUrls.length > 0) {
      setExistingImages(
        item.imagesPreviewUrls.map((url: string, index: number) => ({
          id: -Date.now() - index,
          url,
        })),
      );
    }
  }, [item]);

  const validateField = (
    name: string,
    value: string,
    index?: number,
  ): string => {
    switch (name) {
      case "title":
        if (!value.trim()) return "Title is required";
        if (value.length > 200) return "Title must be 200 characters or less";
        return "";
      case "glossaryDescription":
        if (value && value.length > 1000)
          return "Glossary Description must be 1000 characters or less";
        return "";
      case "date":
        if (!value) return "Date is required";
        if (!dayjs(value).isValid()) return "Invalid date format";
        return "";
      case "address":
        if (value && value.length > 100)
          return "Address must be 100 characters or less";
        return "";
      case "city":
        if (value && value.length > 100)
          return "City must be 100 characters or less";
        return "";
      case "state":
        if (value && value.length > 100)
          return "State must be 100 characters or less";
        return "";
      case "country":
        if (value && value.length > 100)
          return "Country must be 100 characters or less";
        return "";
      case "pin":
        if (value && !/^[0-9A-Za-z\s-]{0,20}$/.test(value))
          return "PIN code must be alphanumeric and 20 characters or less";
        return "";
      case "sectionTitle":
        if (index !== undefined && value && value.length > 200) {
          return "Section title must be 200 characters or less";
        }
        return "";
      default:
        return "";
    }
  };

  const validateImageFile = (file: File): string => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      return "Only JPEG, PNG, GIF, and WebP images are allowed";
    }

    if (file.size > MAX_FILE_SIZE) {
      return "Image size must be less than 5MB";
    }

    return "";
  };

  const validateForm = (): { isValid: boolean; errors: ValidationErrors } => {
  const newErrors: ValidationErrors = {};
  const sectionErrors: string[] = [];

  newErrors.title = validateField("title", title);
  newErrors.date = validateField("date", date);
  newErrors.address = validateField("address", address);
  newErrors.city = validateField("city", city);
  newErrors.state = validateField("state", state);
  newErrors.country = validateField("country", country);
  newErrors.pin = validateField("pin", pin);

  sections.forEach((section, index) => {
    const titleError = validateField("sectionTitle", section.title, index);
    if (titleError) {
      sectionErrors[index] = titleError;
    }
  });

  if (sectionErrors.some((err) => err)) {
    newErrors.sections = sectionErrors;
  }

  // const totalImages = existingImages.length + newImages.length;
  // if (totalImages === 0) {
  //   newErrors.images = "At least 1 image is required";
  // }

  setErrors(newErrors);

  const isValid = !Object.values(newErrors).some((error) =>
    Array.isArray(error) ? error.some((e) => e) : error,
  );

  return { isValid, errors: newErrors };
};

  const updateSection = (
    index: number,
    key: keyof SectionState,
    value: string,
  ) => {
    setSections((prev) => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [key]: value };
      return copy;
    });

    const fieldName = key === "title" ? "sectionTitle" : "sectionDescription";
    const error = validateField(fieldName, value, index);

    setErrors((prev) => {
      const newErrors = { ...prev };
      if (!newErrors.sections) newErrors.sections = [];

      const sectionErrors = [...(newErrors.sections || [])];
      sectionErrors[index] = error;
      newErrors.sections = sectionErrors;

      return newErrors;
    });
  };

  const handleDeleteSection = (index: number) => {
    setSections((prev) => {
      const copy = [...prev];
      copy[index] = { title: "", description: "" };
      return copy;
    });

    setVisibleSections((prev) => Math.max(1, prev - 1));
  };

  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    fieldName: string,
    value: string,
  ) => {
    setter(value);
    setTouched((prev) => ({ ...prev, [fieldName]: true }));

    const error = validateField(fieldName, value);
    setErrors((prev) => ({ ...prev, [fieldName]: error }));
  };

  const handleDeleteExistingImage = async (image: ExistingImage) => {
  if (!item?.id) return;

//   const totalImages = existingImages.length + newImages.length;


//   if (totalImages <= 1) {
//   notifications.show({
//     title: "Image Required",
//     message: "At least 1 image is required",
//     color: "red",
//   });

//   return; 
// }

  if (image.id < 0) {
    setExistingImages((prev) => prev.filter((img) => img.id !== image.id));
    return;
  }

  try {
    setExistingImages((prev) =>
      prev.map((img) =>
        img.id === image.id ? { ...img, deleting: true } : img,
      ),
    );

    const deleteRes = await deleteGlossaryImage({
      detId: image.id,
      plant,
    }).unwrap();

    setExistingImages((prev) =>
      prev.filter((img) => img.id !== image.id),
    );

    notifications.show({
      message: deleteRes.message,
      color: "green",
    });
  } catch (error: any) {
    console.error("Failed to delete image:", error);

    setExistingImages((prev) =>
      prev.map((img) =>
        img.id === image.id ? { ...img, deleting: false } : img,
      ),
    );

    notifications.show({
      message: (error as any)?.data?.message || "An error occurred",
      color: "red",
    });
  }
};

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    // Handle multiple file selection
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      const error = validateImageFile(file);
      if (error) {
        continue;
      }

      const totalImages = existingImages.length + newImages.length;
      if (totalImages >= MAX_IMAGES) {
        break;
      }

      if (item?.id) {
        try {
          const preview = URL.createObjectURL(file);

          // Add to newImages with uploading state
          setNewImages((prev) => [
            ...prev,
            {
              file,
              preview,
              uploading: true,
            },
          ]);

          const formData = new FormData();
          formData.append("plant", plant);
          formData.append("hdrId", item.id.toString());
          formData.append("files", file);

          const result = await uploadGlossaryImage(formData).unwrap();


          // Remove from newImages
          setNewImages((prev) =>
            prev.filter((_, idx) => idx !== prev.length - 1),
          );
          URL.revokeObjectURL(preview);

          // Extract the image info from the response with proper typing
          interface UploadResponse {
            results?: Array<{
              id?: number;
              glossaryImage?: string;
              url?: string;
            }>;
            id?: number;
            imageId?: number;
            url?: string;
            imageUrl?: string;
            glossaryImage?: string;
          }

          let newImage: ExistingImage;

          const imageData = Array.isArray(result.results) && result.results.length > 0
            ? result.results[0]
            : result.results ?? {};
          newImage = {
            id: imageData?.id || Date.now(),
            url: imageData?.glossaryImage || imageData?.url || imageData?.imageUrl || preview,
          };

          // Add to existingImages
          setExistingImages((prev) => [...prev, newImage]);
          setErrors((prev) => ({
          ...prev,
         images: undefined,
}));

          notifications.show({
            message: result.message,
            color: "green",
          });
        } catch (error: any) {
          console.error("Upload failed:", error);

          // Remove the failed upload from newImages
          setNewImages((prev) =>
            prev.filter((_, idx) => idx !== prev.length - 1),
          );

          notifications.show({
            message: (error as any)?.data?.message || "An error occurred",
            color: "red",
          });
        }
      } else {
        // For new glossary (no ID yet)
        const preview = URL.createObjectURL(file);
        setNewImages((prev) => {
  const updated = [
    ...prev,
    {
      file,
      preview,
    },
  ];

  
  // if (updated.length > 0) {
  //   setErrors((prevErrors) => ({
  //     ...prevErrors,
  //     images: undefined,
  //   }));
  // }

  return updated;
});
      }
    }

    // Clear the input
    e.target.value = "";
  };

  const handleRemoveNewImage = (index: number) => {
    setNewImages((prev) => {
      const imageToRemove = prev[index];
      URL.revokeObjectURL(imageToRemove.preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();

  if (isSubmitting) return;

  // Mark required fields as touched
  setTouched((prev) => ({
    ...prev,
    title: true,
    date: true,
  }));

  const { isValid, errors: validationErrors } = validateForm();

  if (!isValid) {
  return;
}

  setIsSubmitting(true);

  try {
    const payload: any = {
      plant: process.env.REACT_APP_PLANT,
      glossaryName: title.trim(),
      glossaryDescription: glossaryDescription.trim(),
      glossaryDescription2: glossaryDescription2.trim(),
      glossaryDate: dayjs(date).format("DD/MM/YYYY"),
      address: address.trim(),
      city: city.trim(),
      state: state.trim(),
      country: country.trim(),
      pin: pin.trim(),
    };

    sections.forEach((s, i) => {
      payload[`section${i + 1}Title`] = s.title.trim();
      payload[`section${i + 1}Description`] = s.description.trim();
    });

    const formData = new FormData();
    formData.append("data", JSON.stringify(payload));

    // Only append files for CREATE mode
   if (!item?.id) {

  const files = newImages.map(img => img.file);

  if (files.length === 0) {
    formData.append("file", new Blob([]), "empty.jpg");
  } else {
    files.forEach(file => formData.append("file", file));
  }

}
    await onSave(formData);

    onCancel();
  } catch (error) {
    console.error("Submission error:", error);
  } finally {
    setIsSubmitting(false);
  }
};

  const showError = (fieldName: string) => {
    return touched[fieldName] && errors[fieldName];
  };

  return (
    <>
      <button onClick={onCancel} className="mb-3 text-blue-600">
        ← Back
      </button>

      <h3 className="mb-4 text-lg font-semibold">
        {item ? "Edit Gallery" : "Add Gallery"}
      </h3>

      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-lg border shadow-sm p-6 space-y-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  {/* Date */}
  <div>
    <label className="block mb-1 font-medium">Date *</label>
    <input
      type="date"
      value={date}
      onChange={(e) => handleFieldChange(setDate, "date", e.target.value)}
      style={fieldStyle}
      onBlur={() => setTouched((prev) => ({ ...prev, date: true }))}
    />
    {showError("date") && <div style={errorStyle}>{errors.date}</div>}
  </div>

  {/* Title */}
  <div>
    <label className="block mb-1 font-medium">Title *</label>
    <input
      value={title}
      onChange={(e) =>
        handleFieldChange(setTitle, "title", e.target.value)
      }
      style={fieldStyle}
      maxLength={200}
      onBlur={() => setTouched((prev) => ({ ...prev, title: true }))}
    />
    <div className="text-right text-xs text-gray-500 mt-1">
      {title.length}/200
    </div>
    {showError("title") && <div style={errorStyle}>{errors.title}</div>}
  </div>
</div>


        <div>
          <label className="block mb-1 font-medium">Gallery Description 1</label>
          <textarea
            value={glossaryDescription}
            onChange={(e) =>
              handleFieldChange(
                setGlossaryDescription,
                "glossaryDescription",
                e.target.value,
              )
            }
            style={{ ...fieldStyle, minHeight: 80 }}
            maxLength={1000}
          />
          <div className="text-right text-xs text-gray-500 mt-1">
            {glossaryDescription.length}/1000
          </div>
        </div>

        <div>
          <label className="block mb-1 font-medium">
            Gallery Description 2
          </label>
          <textarea
            value={glossaryDescription2}
            onChange={(e) =>
              handleFieldChange(
                setGlossaryDescription2,
                "glossaryDescription2",
                e.target.value,
              )
            }
            style={{ ...fieldStyle, minHeight: 100 }}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 font-medium">Address</label>
            <input
              value={address}
              onChange={(e) =>
                handleFieldChange(setAddress, "address", e.target.value)
              }
              style={fieldStyle}
              maxLength={100}
              onBlur={() => setTouched((prev) => ({ ...prev, address: true }))}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {address.length}/100
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium">City</label>
            <input
              value={city}
              onChange={(e) =>
                handleFieldChange(setCity, "city", e.target.value)
              }
              style={fieldStyle}
              maxLength={100}
              onBlur={() => setTouched((prev) => ({ ...prev, city: true }))}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {city.length}/100
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium">State</label>
            <input
              value={state}
              onChange={(e) =>
                handleFieldChange(setState, "state", e.target.value)
              }
              style={fieldStyle}
              maxLength={100}
              onBlur={() => setTouched((prev) => ({ ...prev, state: true }))}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {state.length}/100
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium">Country</label>
            <input
              value={country}
              onChange={(e) =>
                handleFieldChange(setCountry, "country", e.target.value)
              }
              style={fieldStyle}
              maxLength={100}
              onBlur={() => setTouched((prev) => ({ ...prev, country: true }))}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {country.length}/100
            </div>
          </div>

          <div>
            <label className="block mb-1 font-medium">PIN Code</label>
            <input
              value={pin}
              onChange={(e) => handleFieldChange(setPin, "pin", e.target.value)}
              style={fieldStyle}
              maxLength={20}
              onBlur={() => setTouched((prev) => ({ ...prev, pin: true }))}
            />
            <div className="text-right text-xs text-gray-500 mt-1">
              {pin.length}/20
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {sections.slice(0, visibleSections).map((s, i) => (
            <div key={i} className="border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <label className="font-medium">Section {i + 1} Title</label>
                {i === visibleSections - 1 && i > 0 && (
                  <button
                    type="button"
                    onClick={() => handleDeleteSection(i)}
                    className="text-red-500 hover:text-red-700"
                    title="Delete Section"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <input
                value={s.title}
                onChange={(e) => updateSection(i, "title", e.target.value)}
                style={fieldStyle}
                maxLength={200}
                onBlur={() =>
                  setTouched((prev) => ({
                    ...prev,
                    [`section${i}Title`]: true,
                  }))
                }
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {s.title.length}/200
              </div>

              <label className="block mt-3 mb-1 font-medium">
                Section {i + 1} Description
              </label>
              <textarea
                value={s.description}
                onChange={(e) =>
                  updateSection(i, "description", e.target.value)
                }
                style={{ ...fieldStyle, minHeight: 80 }}
              />
            </div>
          ))}

          {visibleSections < MAX_SECTIONS && (
            <button
              type="button"
              onClick={() => setVisibleSections((v) => v + 1)}
              className="text-blue-600 text-sm hover:underline"
            >
              + Add Section
            </button>
          )}
        </div>

        <div className="border-t pt-4">
          <label className="block mb-2 font-medium text-lg">
    Images (Optional)
</label>
{/* <div className="text-xs text-yellow-500 mb-2">
  ⚠️ At least one required image must be uploaded.
</div> */}

          {existingImages.length > 0 && (
            <div className="mb-4">
              <label className="block mb-2 text-sm text-gray-600">
                Current Images
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {existingImages.map((img) => (
                  <div
                    key={img.id}
                    className="relative group border rounded-lg overflow-hidden"
                  >
                    <img
                      src={img.url}
                      alt="Glossary"
                      className="w-full h-32 object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleDeleteExistingImage(img)}
                      disabled={img.deleting}
                      className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                      title="Delete Image"
                    >
                      <X size={16} />
                    </button>
                    {img.deleting && (
                      <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {newImages.length > 0 && (
            <div className="mb-4">
              <label className="block mb-2 text-sm text-gray-600">
                {item?.id ? "Uploading Images" : "New Images"}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {newImages.map((img, index) => (
                  <div
                    key={index}
                    className="relative group border rounded-lg overflow-hidden"
                  >
                    <img
                      src={img.preview}
                      alt="Preview"
                      className="w-full h-32 object-cover"
                    />
                    {img.uploading ? (
                      <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleRemoveNewImage(index)}
                        className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-500 hover:text-red-700 shadow"
                        title="Remove Image"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {existingImages.length + newImages.length < MAX_IMAGES && (
            <div className="mt-2">
              <label className="block mb-2 text-sm text-gray-600">
                {item?.id ? "Add More Images" : "Upload Images"}
              </label>
              <div className="text-xs text-gray-500 mb-2">
                Max {MAX_IMAGES} images total, 5MB each. Allowed: JPEG, PNG,
                GIF, WebP
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
            </div>
          )}

          <div className="mt-2 text-xs text-gray-500">
            {existingImages.length + newImages.length} / {MAX_IMAGES} images
          </div>
        </div>
        {/* {errors.images && (
  <div className="mt-2 text-sm text-red-600">
    {errors.images}
  </div>
)} */}

        <div className="sticky bottom-0 -mx-6 mt-6 border-t bg-white px-6 py-4 flex justify-end gap-3 shadow-[0_-2px_6px_rgba(0,0,0,0.05)]">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Saving..."
              : item
                ? "Update Gallery"
                : "Create Gallery"}
          </button>
        </div>
      </form>
    </>
  );
};

export default GlossaryForm;
